import { useEffect, useState } from 'react';
import { payApiClient } from '@/api/client';
import { ApiRequestError } from '@/api/errors';
import type { TestBenchPaymentTimeline } from '@/api/models';
import { isTerminalStatus, TIMELINE_POLL_INTERVAL_MS } from './domain';

interface TestBenchTimelineState {
  timeline: TestBenchPaymentTimeline | undefined;
  loading: boolean;
  error: ApiRequestError | undefined;
  refresh: () => void;
}

const genericErrorMessage = 'Servicio no disponible temporalmente. Inténtelo de nuevo más tarde.';

/**
 * Polls `GET /v1/admin/test-bench/payments/{intentId}/timeline` every
 * {@link TIMELINE_POLL_INTERVAL_MS} while `intentId` is set, stopping as
 * soon as the returned status is terminal (captured/failed/cancelled/
 * expired) or the component unmounts. `refresh()` forces one extra fetch
 * without waiting for the next tick.
 */
export function useTestBenchTimeline(intentId: string | undefined): TestBenchTimelineState {
  const [timeline, setTimeline] = useState<TestBenchPaymentTimeline>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiRequestError>();
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (!intentId) {
      setTimeline(undefined);
      setError(undefined);
      return;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    setLoading(true);

    function stopPolling() {
      if (intervalId !== undefined) {
        clearInterval(intervalId);
        intervalId = undefined;
      }
    }

    function fetchOnce() {
      payApiClient
        .getTestBenchPaymentTimeline(intentId as string)
        .then((result) => {
          if (cancelled) return;
          setTimeline(result);
          setError(undefined);
          setLoading(false);
          if (isTerminalStatus(result.status)) stopPolling();
        })
        .catch((caught: unknown) => {
          if (cancelled) return;
          setError(caught instanceof ApiRequestError ? caught : new ApiRequestError(0, 'unknown', genericErrorMessage));
          setLoading(false);
        });
    }

    fetchOnce();
    intervalId = setInterval(fetchOnce, TIMELINE_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [intentId, refreshToken]);

  return { timeline, loading, error, refresh: () => setRefreshToken((token) => token + 1) };
}
